<script lang="ts">
  import { createQueries, createQuery } from '@tanstack/svelte-query';
  import Link from '$components/ui/link.svelte';
  import Input from '$components/ui/input.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import SearchProductGrid from './search-product-grid.svelte';

  import { layoutPanel } from '$lib/stores/layout-panel';

  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { shopify } from '$lib/shopify';
  import { buildPath } from '$lib/utils/paths';
  import { recentlyViewed } from '$lib/recently-viewed';
  import { SEARCH_PARAM_KEY } from '$lib/constants';
  import { filterConfiguration } from '$lib/stores/content';
  import { createDebouncedValue } from '$lib/stores/debounced';

  import type {
    GetPredictiveSearchQuery,
    GetPredictiveSearchQueryVariables,
    ProductCardFragment
  } from '$gql/storefront/operations';
  import type { LanguageCode } from '$gql/storefront/schemas';
  import { lang } from '$lib/lang';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';

  let className: string | undefined = undefined;
  export { className as class };

  /* GraphQL */ `
    query getPredictiveSearch($query: String!, $lang: LanguageCode, $country: CountryCode)
    @inContext(language: $lang, country: $country) {
      predictiveSearch(
        query: $query
        limit: 5
        limitScope: EACH
        searchableFields: [PRODUCT_TYPE, TAG, TITLE, VARIANTS_TITLE]
      ) {
        queries {
          text
        }
        products {
          ...ProductCard
        }
      }
    }
  `;

  const [search, debouncedSearch] = createDebouncedValue('', 300);
  let inputRef: HTMLInputElement;
  let formElement: HTMLFormElement;

  let scrollHeight = 0;

  $: predictiveSearchQuery = createQuery<
    GetPredictiveSearchQuery,
    GetPredictiveSearchQueryVariables
  >({
    enabled: !!$debouncedSearch,
    staleTime: 5 * 60 * 1000,
    queryKey: ['predictive-search', $debouncedSearch],
    queryFn: async () =>
      await $shopify.getPredictiveSearch({
        query: $debouncedSearch,
        lang: $lang.toUpperCase() as LanguageCode,
        country: parseCountryAsCode($optimalUSCountry)
      })
  });

  $: recentlyViewedProductsQuery = createQueries(
    $recentlyViewed.slice(0, 6).map((handle) => ({
      staleTime: 5 * 60 * 1000,
      queryKey: ['productCard', handle],
      queryFn: async () => {
        return await $shopify.getProductCard({
          handle,
          lang: $lang.toUpperCase() as LanguageCode,
          country: parseCountryAsCode($optimalUSCountry)
        });
      }
    }))
  );

  $: recentlyViewedProducts = $recentlyViewedProductsQuery
    .map((q) => q.data?.product)
    .filter(Boolean) as ProductCardFragment[];
  $: predictiveSearchProducts = $predictiveSearchQuery.data?.predictiveSearch?.products;
  $: predictiveSearchSuggestions = $predictiveSearchQuery.data?.predictiveSearch?.queries;

  $: products =
    !!$debouncedSearch && !!predictiveSearchProducts?.length
      ? predictiveSearchProducts
      : recentlyViewedProducts;

  $: loading =
    $recentlyViewedProductsQuery.some((q) => q.isLoading) ||
    (!!$debouncedSearch && $predictiveSearchQuery?.isLoading);
</script>

<svelte:window bind:scrollY={scrollHeight} />

<div class={cn('pt-4 pb-10 lg:pb-8', className)}>
  <form bind:this={formElement} action={$buildPath({ path: '/search' })}>
    <Input
      autofocus
      id={SEARCH_PARAM_KEY}
      name={SEARCH_PARAM_KEY}
      bind:ref={inputRef}
      bind:value={$search}
      placeholder={$t('search.input-placeholder')}
      class="mx-2 lg:mx-0"
      autocomplete="off"
    />
    <div class="mt-8 flex flex-col lg:flex-row gap-8">
      <div
        class={cn(
          'lg:flex flex-col lg:gap-2 gap-3 lg:min-w-[200px] lg:max-w-[200px] px-2 lg:px-0',
          $search ? 'hidden' : 'flex'
        )}
      >
        {#if loading}
          <Skeleton class="w-1/3 h-4 rounded-sm" />
          {#each Array(5) as _}
            {@const w = Math.max(Math.random() * 100, 50)}
            <div style:--w={w + '%'}>
              <Skeleton class="h-5 rounded-sm w-[--w]" />
            </div>
          {/each}
        {:else}
          <p class="text-sm font-semibold text-primary/40">
            {#if !!$debouncedSearch && !!predictiveSearchSuggestions?.length}
              {$t('search.suggestions')}
            {:else}
              {$t('search.top-searches')}
            {/if}
          </p>
          {#if !!$debouncedSearch && !!predictiveSearchSuggestions?.length}
            {#each predictiveSearchSuggestions || [] as suggestion}
              {@const suggestionText =
                suggestion.text.charAt(0).toUpperCase() + suggestion.text.slice(1)}
              <Link
                as="button"
                type="button"
                class="text-lg font-semibold line-clamp-2 text-ellipsis inline text-start"
                on:click={() => {
                  layoutPanel.close();
                  inputRef.value = suggestionText;
                  formElement.requestSubmit();
                }}
              >
                {suggestionText}
              </Link>
            {/each}
          {:else}
            {#each $filterConfiguration.top_searches as suggestion}
              <Link
                as="button"
                type="button"
                class="text-lg font-semibold"
                on:click={() => {
                  layoutPanel.close();
                  inputRef.value = suggestion.text;
                  formElement.requestSubmit();
                }}
              >
                {suggestion.text}
              </Link>
            {/each}
          {/if}
        {/if}
      </div>

      <div class="grow">
        <p class="text-sm lg:px-0 px-2 text-primary/40 mb-3 font-semibold">
          {#if !!$debouncedSearch}
            {$t('search.results')}
          {:else if !!recentlyViewedProducts.length}
            {$t('search.recently-viewed')}
          {:else}
            &nbsp;
          {/if}
        </p>
        <SearchProductGrid
          {products}
          {loading}
          isRecentlyViewed={!(!!$debouncedSearch && !!predictiveSearchProducts?.length)}
          noResults={!!$debouncedSearch && !predictiveSearchProducts?.length && !loading}
          seeAll={!!$debouncedSearch && !!predictiveSearchProducts?.length}
        />
      </div>
    </div>
  </form>
</div>
